import {
  type ProductSettings,
  useProductSettings,
} from '@workspace/utils-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { AuthProvider } from '../../../asset-manager/src/providers/AuthProvider';
import useIdentity from '../useIdentity';

/**
 * Decodes a JWT token and extracts a claim value.
 * Returns undefined if the token is invalid or the claim doesn't exist.
 */
function getClaimFromToken(
  token: string | undefined,
  claimName: string,
): string | undefined {
  if (!token) return undefined;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;
    const payload = JSON.parse(atob(parts[1]));
    return payload[claimName] as string | undefined;
  } catch {
    return undefined;
  }
}

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({
  instanceId,
  children,
}: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } =
    useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (
      error &&
      (error as any)?.message === 'login_required' &&
      settings?.workspaceUrl
    ) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    // Try to get email from profile first, then fall back to access token
    // Some OIDC configurations put the email in the access token but not the ID token
    const email =
      (user.profile?.email as string | undefined) ??
      getClaimFromToken(user.access_token, 'email');
    const organisationId = user.profile?.organisationId as string | undefined;
    return (
      <AuthProvider
        getAccessToken={getAccessToken}
        instanceId={instanceId}
        email={email}
        organisationId={organisationId}
      >
        {children}
      </AuthProvider>
    );
  }

  return (
    <div className="unauthenticated-container">
      <p>Authentication required...</p>
    </div>
  );
}
